import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { AreaChart, Area, ResponsiveContainer } from "recharts";
import {
  ArrowRight, Sparkles, Instagram, LineChart, Brain, Film, Activity,
  TrendingUp, Users, Eye, Trophy, CheckCircle2,
} from "lucide-react";
import { PublicNav } from "@/components/PublicNav";
import { Footer } from "@/components/Footer";
import { PricingCards } from "@/pages/Pricing";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useT } from "@/i18n";

const demoChart = Array.from({ length: 30 }, (_, i) => ({ v: 22000 + i * 90 + Math.sin(i / 2) * 300 }));

const DemoDashboard = () => {
  const { t } = useT();
  return (
    <div className="rounded-2xl border border-border bg-card p-4 shadow-2xl sm:p-5">
      <div className="flex items-center gap-2 border-b border-border pb-3">
        <div className="flex gap-1.5"><span className="h-3 w-3 rounded-full bg-rose-400" /><span className="h-3 w-3 rounded-full bg-amber-400" /><span className="h-3 w-3 rounded-full bg-emerald-400" /></div>
        <div className="ml-3 flex items-center gap-1.5 text-xs text-muted-foreground"><Instagram className="h-3.5 w-3.5" />@marco.travel</div>
      </div>
      <div className="mt-4 grid grid-cols-3 gap-3">
        {[
          { icon: Users, label: "Follower", val: "24.582", d: "+1,77%" },
          { icon: Eye, label: "Views", val: "1,2M", d: "+12%" },
          { icon: Activity, label: "Engagement", val: "8,4%", d: "+3%" },
        ].map((m) => (
          <div key={m.label} className="rounded-xl border border-border bg-background/60 p-3">
            <div className="flex items-center gap-1 text-[10px] uppercase text-muted-foreground"><m.icon className="h-3 w-3" />{m.label}</div>
            <div className="mt-1 font-display text-lg font-bold">{m.val}</div>
            <div className="text-[11px] font-medium text-emerald-500">{m.d}</div>
          </div>
        ))}
      </div>
      <div className="mt-3 rounded-xl border border-border bg-background/60 p-3">
        <div className="mb-1 text-xs text-muted-foreground">Crescita follower · 30 giorni</div>
        <ResponsiveContainer width="100%" height={90}>
          <AreaChart data={demoChart}>
            <defs><linearGradient id="dg" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#FF7A00" stopOpacity={0.4} /><stop offset="100%" stopColor="#FF7A00" stopOpacity={0} /></linearGradient></defs>
            <Area type="monotone" dataKey="v" stroke="#FF7A00" strokeWidth={2} fill="url(#dg)" />
          </AreaChart>
        </ResponsiveContainer>
      </div>
      <div className="mt-3 flex items-center gap-3 rounded-xl border border-brand-500/20 bg-brand-500/5 p-3">
        <Trophy className="h-5 w-5 shrink-0 text-amber-500" />
        <div className="min-w-0"><div className="text-xs font-medium">Miglior Reel · Growth Score 87</div><div className="truncate text-xs text-muted-foreground">+72% di views rispetto alla tua media</div></div>
      </div>
    </div>
  );
};

export function Landing() {
  const { t } = useT();
  const navigate = useNavigate();

  const steps = [
    { icon: Instagram, title: t("landing.how1Title"), desc: t("landing.how1Desc") },
    { icon: LineChart, title: t("landing.how2Title"), desc: t("landing.how2Desc") },
    { icon: Sparkles, title: t("landing.how3Title"), desc: t("landing.how3Desc") },
  ];
  const features = [
    { icon: Film, title: t("landing.sectionContentTitle"), desc: t("landing.sectionContentDesc") },
    { icon: TrendingUp, title: t("landing.sectionGrowthTitle"), desc: t("landing.sectionGrowthDesc") },
    { icon: Brain, title: t("landing.sectionAITitle"), desc: t("landing.sectionAIDesc") },
    { icon: Sparkles, title: t("landing.sectionReelTitle"), desc: t("landing.sectionReelDesc") },
    { icon: Activity, title: t("landing.sectionMonitorTitle"), desc: t("landing.sectionMonitorDesc") },
  ];
  const faqs = t("landing.faq") || [];

  return (
    <div className="min-h-screen bg-background">
      <PublicNav />

      {/* Hero */}
      <section className="relative overflow-hidden gp-hero-gradient">
        <div className="absolute inset-0 gp-grid-bg opacity-40" />
        <div className="relative mx-auto grid max-w-7xl items-center gap-12 px-4 py-16 sm:px-6 lg:grid-cols-2 lg:gap-8 lg:px-8 lg:py-28">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <div className="inline-flex items-center gap-2 rounded-full border border-border bg-card/70 px-3 py-1 text-xs font-medium backdrop-blur">
              <Sparkles className="h-3.5 w-3.5 text-brand-500" /> {t("landing.trustedBy")}
            </div>
            <h1 className="mt-5 font-display text-4xl font-bold leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl">
              {t("landing.heroTitle")}
            </h1>
            <p className="mt-5 max-w-lg text-lg leading-relaxed text-muted-foreground">{t("landing.heroSubtitle")}</p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <Button onClick={() => navigate("/register")} data-testid="hero-cta-primary" className="h-12 rounded-xl bg-brand-500 px-6 text-base hover:bg-brand-600 text-black">
                {t("landing.ctaPrimary")} <ArrowRight className="ml-1.5 h-4 w-4" />
              </Button>
              <Button variant="outline" asChild className="h-12 rounded-xl px-6 text-base">
                <a href="#how" data-testid="hero-cta-secondary">{t("landing.ctaSecondary")}</a>
              </Button>
            </div>
          </motion.div>
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5, delay: 0.15 }}>
            <DemoDashboard />
          </motion.div>
        </div>
      </section>

      {/* How it works */}
      <section id="how" className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-2xl text-center">
          <h2 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">{t("landing.howTitle")}</h2>
          <p className="mt-3 text-lg text-muted-foreground">{t("landing.howSubtitle")}</p>
        </div>
        <div className="mt-14 grid gap-6 md:grid-cols-3">
          {steps.map((s, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
              className="relative rounded-2xl border border-border bg-card p-7">
              <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-brand-500 to-brand-700 text-white"><s.icon className="h-6 w-6" /></div>
              <div className="absolute right-6 top-6 font-display text-4xl font-bold text-muted/40">0{i + 1}</div>
              <h3 className="font-display text-lg font-semibold">{s.title}</h3>
              <p className="mt-2 text-sm text-muted-foreground">{s.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Features */}
      <section id="features" className="border-y border-border bg-card/40">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {features.map((f, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.06 }}
                className="rounded-2xl border border-border bg-card p-7 hover:border-brand-500/40 transition-colors">
                <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl bg-brand-500/10 text-brand-700 dark:text-brand-400"><f.icon className="h-5 w-5" /></div>
                <h3 className="font-display text-lg font-semibold">{f.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{f.desc}</p>
              </motion.div>
            ))}
            <div className="flex flex-col justify-center rounded-2xl border border-brand-500/20 bg-gradient-to-br from-brand-500/10 to-transparent p-7">
              <div className="flex items-center gap-2 text-brand-700 dark:text-brand-400"><Sparkles className="h-5 w-5" /><span className="text-xs font-mono uppercase tracking-widest">DATI → INSIGHT → AZIONE</span></div>
              <p className="mt-3 font-display text-xl font-semibold leading-snug">412Level analizza il tuo Instagram e ti dice cosa pubblicare dopo.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Demo section */}
      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
        <div className="grid items-center gap-12 lg:grid-cols-2">
          <div>
            <h2 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">{t("landing.demoTitle")}</h2>
            <p className="mt-3 text-lg text-muted-foreground">{t("landing.demoDesc")}</p>
            <ul className="mt-6 space-y-3">
              {["Growth Score per ogni contenuto", "Pattern rilevati automaticamente", "Idee e script pronti all'uso"].map((x) => (
                <li key={x} className="flex items-center gap-2.5 text-sm"><CheckCircle2 className="h-4 w-4 text-emerald-500" />{x}</li>
              ))}
            </ul>
            <Button onClick={() => navigate("/register")} className="mt-8 h-11 rounded-xl bg-brand-500 hover:bg-brand-600 text-black">{t("landing.ctaPrimary")}</Button>
          </div>
          <DemoDashboard />
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="border-y border-border bg-card/40">
        <div className="mx-auto max-w-6xl px-4 py-20 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">{t("pricing.title")}</h2>
            <p className="mt-3 text-lg text-muted-foreground">{t("pricing.subtitle")}</p>
          </div>
          <div className="mt-14"><PricingCards /></div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="mx-auto max-w-3xl px-4 py-20 sm:px-6 lg:px-8">
        <h2 className="text-center font-display text-3xl font-bold tracking-tight sm:text-4xl">{t("landing.faqTitle")}</h2>
        <Accordion type="single" collapsible className="mt-10">
          {(Array.isArray(faqs) ? faqs : []).map((f, i) => (
            <AccordionItem key={i} value={`f${i}`} data-testid={`faq-${i}`}>
              <AccordionTrigger className="text-left font-medium">{f.q}</AccordionTrigger>
              <AccordionContent className="text-muted-foreground">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      {/* Final CTA */}
      <section className="mx-auto max-w-7xl px-4 pb-24 sm:px-6 lg:px-8">
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-brand-500 via-brand-700 to-brand-800 p-10 text-center sm:p-16">
          <div className="absolute inset-0 gp-grid-bg opacity-20" />
          <div className="relative">
            <h2 className="font-display text-3xl font-bold tracking-tight text-white sm:text-4xl">{t("landing.finalCtaTitle")}</h2>
            <p className="mx-auto mt-3 max-w-md text-white/90">{t("landing.finalCtaDesc")}</p>
            <Button onClick={() => navigate("/register")} data-testid="final-cta" className="mt-8 h-12 rounded-xl bg-white px-8 text-base text-brand-700 hover:bg-white/90">
              {t("landing.ctaPrimary")} <ArrowRight className="ml-1.5 h-4 w-4" />
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
